import React, { useState } from "react";
import { installAgent, uninstallAgent } from "../../api/mcp";

interface AgentInstallerRowProps {
  agent: string;
  label: string;
  // Indicates whether ANY token has been minted for this org (gates the
  // install button — without a token the installer can't write the
  // ~/.agentguard/mcp-token file).
  hasToken: boolean;
}

export function AgentInstallerRow({ agent, label, hasToken }: AgentInstallerRowProps) {
  const [busy, setBusy] = useState<"install" | "uninstall" | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [output, setOutput] = useState<string | null>(null);
  const [showOutput, setShowOutput] = useState(false);

  async function handleInstall() {
    setBusy("install");
    setError(null);
    try {
      const r = await installAgent(agent);
      setOutput(r.output);
      setStatus(r.ok ? "Installed" : "Failed");
    } catch (err) {
      setError(err instanceof Error ? err.message : `install ${agent} failed`);
    } finally {
      setBusy(null);
    }
  }

  async function handleUninstall() {
    const reason = window.prompt("Reason for uninstalling? (recorded in audit log)") || "";
    if (!reason.trim()) {
      setError("Uninstall cancelled — reason is required");
      return;
    }
    setBusy("uninstall");
    setError(null);
    try {
      const r = await uninstallAgent(agent, reason.trim());
      setOutput(r.output);
      setStatus(r.ok ? "Uninstalled" : "Failed");
    } catch (err) {
      setError(err instanceof Error ? err.message : `uninstall ${agent} failed`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <tr className="ac-vault-row" data-agent={agent}>
      <td className="ac-vault-row__name">{label}</td>
      <td className="ac-vault-row__masked">
        {status ? <span className="ac-chip">{status}</span> : <span className="ac-dim">—</span>}
      </td>
      <td className="ac-vault-row__actions">
        <button
          className="ac-btn ac-btn--secondary ac-btn--sm"
          onClick={() => void handleInstall()}
          disabled={busy !== null || !hasToken}
          aria-label={`Install ${label}`}
          title={hasToken ? `Install Steerly MCP into ${label}` : "Mint a token first"}
        >
          {busy === "install" ? "Installing…" : "Install"}
        </button>
        <button
          className="ac-btn ac-btn--danger ac-btn--sm"
          onClick={() => void handleUninstall()}
          disabled={busy !== null}
          aria-label={`Uninstall ${label}`}
        >
          {busy === "uninstall" ? "Removing…" : "Uninstall"}
        </button>
        {output && (
          <button
            className="ac-btn ac-btn--secondary ac-btn--sm"
            onClick={() => setShowOutput((v) => !v)}
            aria-label="Show installer output"
          >
            {showOutput ? "Hide output" : "Show output"}
          </button>
        )}
        {error && <span className="ac-error-banner ac-error-banner--inline">{error}</span>}
        {showOutput && output && (
          <pre className="ac-installer-output" aria-label={`${agent} output`}>
            {output}
          </pre>
        )}
      </td>
    </tr>
  );
}
